/**
 * Wrapper para Funcionalidades Restritas
 * 
 * O que faz:
 * - Verifica se o usuário tem acesso à funcionalidade
 * - Exibe componente normal se tiver acesso
 * - Exibe prompt de upgrade se não tiver acesso
 * 
 * Onde é usado:
 * - Envolvendo funcionalidades premium em todos os componentes
 * 
 * Como se conecta:
 * - useFeatureBlocking para verificar permissões
 * - RestrictedFeature para exibir prompt de upgrade
 */

import React from 'react';
import { useFeatureBlocking } from '@/hooks/useFeatureBlocking';
import { RestrictedFeature } from '@/components/ui/RestrictedFeature';

interface RestrictedFeatureWrapperProps {
  children: React.ReactNode;
  feature: keyof Pick<
    ReturnType<typeof useFeatureBlocking>, 
    'canImportContacts' | 'canExportData' | 'canScheduleMessages' | 'canUseInstagram' | 'canUseMedicalRecord'
  >;
  featureName: string;
  requiredPlan: string;
  fallback?: React.ReactNode;
}

export const RestrictedFeatureWrapper: React.FC<RestrictedFeatureWrapperProps> = ({
  children,
  feature,
  featureName,
  requiredPlan,
  fallback
}) => {
  const featureAccess = useFeatureBlocking();
  const hasAccess = featureAccess[feature];

  // Se está carregando, mostra componente normal (evita flash)
  if (featureAccess.isLoading) {
    return <>{children}</>;
  }

  // Se tem acesso, mostra componente normal
  if (hasAccess) {
    return <>{children}</>;
  }

  // Se não tem acesso, mostra prompt de upgrade ou fallback
  if (fallback) {
    return <>{fallback}</>;
  }

  return (
    <RestrictedFeature
      featureName={featureName}
      planName={featureAccess.subscriptionTier}
      showUpgrade={!hasAccess}
      isAdmin={false}
      hasOverride={false}
      onRequestAccess={() => {
        console.log(`Upgrade solicitado para: ${featureName}`);
        // Aqui poderia redirecionar para página de billing
        window.location.href = '/billing';
      }}
      variant="card"
    />
  );
};